import React from "react";
import { Line } from "react-chartjs-2";

const SaaTaaChart = ({ graphData, selectedFilter, isLoadingGraph }) => {
  if (!graphData || !graphData.saa_performance || !graphData.taa_performance) {
    return "No data found";
  }

  // Extract labels (dates) for X-axis
  const labels = graphData.saa_performance.performance_data.map((data) => {
    const date = new Date(data.date); // Parse the date
    return date;
  });

  // Extract SAA and TAA values
  const saaDataset = {
    label: "SAA",
    data: graphData.saa_performance.performance_data.map((item) => item.value),
    borderColor: "#FF0000", // Red
    backgroundColor: "rgba(255, 0, 0, 0.1)", // Light red fill
    tension: 0.4,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointBackgroundColor: "#FF0000",
    fill: true,
  };

  // Prepare TAA dataset (Blur blue line)
  const taaDataset = {
    label: "TAA",
    data: graphData.taa_performance.performance_data.map((item) => item.value),
    borderColor: "#3047EC", // Blue
    backgroundColor: "rgba(48, 71, 236, 0.07)", // Light blue fill
    tension: 0.4,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointBackgroundColor: "#3047EC",
    fill: true,
  };

  const data = {
    labels,
    datasets: [saaDataset, taaDataset],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // No legend
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#111827",
        titleColor: "#fff",
        borderColor: "#374151",
        borderWidth: 1,
        padding: 12,
        displayColors: false, // hoverRadiusHide colored dots
        callbacks: {
          title: (tooltipItems) => {
            const timestamp = tooltipItems[0].parsed.x; // <-- reliable timestamp
            const date = new Date(timestamp);
            if (selectedFilter === "1M" || selectedFilter === "3M") {
              const day = date.getDate().toString().padStart(2, "0");
              const month = date.toLocaleDateString("en-US", {
                month: "short",
              });
              const year = date.getFullYear();
              return `${day} ${month} ${year}`;
            } else {
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const year = date.getFullYear();
              return `${month}/${year}`;
            }
          },
          label: (tooltipItem) => {
            const index = tooltipItem.dataIndex;
            const value = `$${tooltipItem.raw
              .toFixed(2)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

            return value;
          },
          labelTextColor: function (context) {
            const index = context.datasetIndex;
            return ["#DC4646", "#3047EC", "#34C046"][index];
          },
        },
      },
    },
    scales: {
      x: {
        type: "time", // Ensure the x-axis uses time scale
        time: {
          unit:
            selectedFilter === "1M" || selectedFilter === "3M"
              ? "day"
              : "month",
          tooltipFormat:
            selectedFilter === "1M" || selectedFilter === "3M"
              ? "MMM d (EEE)"
              : "MMM yyyy",
        },
        grid: { drawOnChartArea: false, drawTicks: false },
        ticks: {
          display: true,
          color: "black",

          callback: function (value) {
            const date = new Date(value);

            if (selectedFilter === "1M" || selectedFilter === "3M") {
              const day = date.getDate().toString().padStart(2, "0");
              const month = date.toLocaleDateString("en-US", {
                month: "short",
              });

              return [day, month];
            } else {
              const month = date.toLocaleDateString("en-US", {
                month: "short",
              }); // e.g., Jan
              const year = date.getFullYear();
              return [month, year];
            }
          },
          font: {
            size: 12, // Adjust size to fit the labels properly
          },
          maxRotation: 0,
          minRotation: 0,
        },
        border: { color: "black" },
        afterFit: function (scale) {
          scale.height = 60; // Adjust the height to ensure space for two lines
        },
      },
      y: {
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: {
          callback: (value) => `${value.toFixed(2)}`,
          color: "black",
          padding: 10,
        },
        beginAtZero: false,
        min: 0,
        offset: true,
        border: {
          display: false,
        },
      },
    },
  };

  return (
    <div style={{ width: "100%", height: "400px" }}>
      {isLoadingGraph ? (
        <div className="shimmer-container-list mb-4 ">
          {/* Creating 5 rows of shimmer effect */}
          {[...Array(2)].map((_, rowIndex) => (
            <div key={rowIndex} className="shimmer-row-list">
              {/* Creating 7 cells in each row for shimmer */}
              {[...Array(5)].map((_, colIndex) => (
                <div key={colIndex} className="shimmer-cell-list h-20">
                  <div className="shimmer-loader"></div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <Line data={data} options={options} />
      )}
    </div>
  );
};

export default SaaTaaChart;
